import { Trans } from "@lingui/react/macro";
import {
  format,
  differenceInCalendarDays,
  differenceInMinutes,
} from "date-fns";
import { de } from "date-fns/locale";
import is from "is";
import { inflate } from "pako";
import LZString from "lz-string";
import numeral from "../lib/numeral";
import { ExcelDateToFormatedString } from "./helperExcel";
const urlCrypt = require("url-crypt")(process.env.REACT_APP_URLCRYPT_KEY);

export function isEmpty(x) {
  if (Array.isArray(x) || typeof x === "string" || x instanceof String) {
    return x.length === 0;
  }

  if (x instanceof Map || x instanceof Set) {
    return x.size === 0;
  }

  if ({}.toString.call(x) === "[object Object]") {
    return Object.keys(x).length === 0;
  }

  return false;
}

export function isSameText(x, y) {
  if (!x || !y) return false;

  return x.toLowerCase() === y.toLowerCase();
}

export function isEmailIncluded(email, testEmail, testCollaborators) {
  if (!testEmail || !email) return false;
  let isOK = isSameText(email, testEmail);
  if (isOK) return true;
  if (!testCollaborators) return false;
  if (is.string(testCollaborators)) {
    return (
      testCollaborators &&
      testCollaborators.toLowerCase().includes(email.toLowerCase())
    );
  } else if (is.array(testCollaborators)) {
    return testCollaborators.includes(email.toLowerCase());
  }
}

function isObject(obj) {
  // eslint-disable-next-line eqeqeq
  return obj !== undefined && obj !== null && obj.constructor == Object;
}

export function isIsoDate(str) {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
  var d = new Date(str);
  return d.toISOString() === str;
}

export function addTrailingSlash(url) {
  if (!url) return "";

  let newUrl = url;
  newUrl += url.endsWith("/") ? "" : "/";
  return newUrl;
}

export function openInNewTab(url) {
  if (!url) return;

  if (!url.includes("http")) {
    const mainUrl = window.location.origin + "/#";
    url = mainUrl + url;
  }

  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
}

export function openInSameTab(url) {
  const newWindow = window.open(url, "_self", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
export function debounce(func, wait, immediate) {
  var timeout = {};
  return function () {
    var context = this,
      [key, ...args] = arguments;
    if (typeof key !== "string" || !key) {
      key = "default";
    }
    var later = function () {
      timeout[key] = null;
      if (!immediate) func.apply(context, [key, ...args]);
    };
    var callNow = immediate && !timeout[key];
    clearTimeout(timeout[key]);
    timeout[key] = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export function removeHtmlTagsFromText(html) {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText;
}

export function jsonCopy(src) {
  var replacer = function (key, value) {
    return typeof value === "undefined" ? null : value;
  };
  return JSON.parse(JSON.stringify(src, replacer));
}

export function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function getJsonFromCompression(str) {
  if (!str) return {};
  let isString = isJsonString(str);
  if (isString) return getAsJson(str);
  // is it compressed
  let decompressedInfo = decompressJson(str);
  if (isObject(decompressedInfo)) return decompressedInfo;
}

export function getAsJson(str) {
  if (!str) return {};
  if (is.object(str)) return str;

  let result = {};
  try {
    result = JSON.parse(str);
  } catch (e) {
    console.log("Error parsing", e);
    return {};
  }
  return result;
}

export function getAsJsonList(str) {
  if (!str) return [];

  let result = [];
  try {
    result = JSON.parse(str);
  } catch (e) {
    return [];
  }
  return result;
}

export function getSizeInBytes(obj) {
  let str = null;
  if (typeof obj === "string") {
    // If obj is a string, then use it
    str = obj;
  } else {
    // Else, make obj into a string
    str = JSON.stringify(obj);
  }
  // Get the length of the Uint8Array
  const bytes = new TextEncoder().encode(str).length;
  return bytes;
}

export function isNumber(value) {
  return !isNaN(value);
}

export function tryParseInt(str) {
  var retValue = str;
  if (str !== null && is.string(str)) {
    if (str.length > 0) {
      if (!isNaN(str)) {
        retValue = parseInt(str);
      }
    }
  }
  return retValue;
}

export function tryParseFloat(str) {
  if (is.number(str)) return str;
  let oldStr = str;
  let retValue = str;
  if (str !== null && is.string(str)) {
    const isPercentage = str.includes("%");
    if (isPercentage) retValue = str.replace("%", "");
    if (str.length > 0) {
      if (!isNaN(str)) {
        retValue = parseFloat(str);
      }
      if (isPercentage && !isNaN(retValue)) retValue /= 100;
    }
  }
  if (isNaN(retValue)) retValue = oldStr;
  return retValue;
}

export function tryParseBoolean(str) {
  var retValue = str;
  if (str !== null && is.string(str)) {
    if (str.toLowerCase() === "true") retValue = true;
    if (str.toLowerCase() === "false") retValue = false;
  }
  return retValue;
}

function getRandomInt(max) {
  let randomNumber = Math.floor(Math.random() * max);
  if (randomNumber === 0) randomNumber = 1;
  return randomNumber;
}

export function getDummyImage() {
  return (
    "https://airrange-content.s3.eu-central-1.amazonaws.com/graphics/patterns/webp/pattern_" +
    getRandomInt(63) +
    ".webp"
  );
}

export function isItAMainView(view, ignore = []) {
  // is it a main view
  const result = [
    "home",
    "files",
    "services",
    "outbound",
    "inbound",
    "tables",
    "projects",
    "recents",
    "automations",
  ].includes(view);
  // ignore
  if (ignore?.includes(view)) return false;

  return result;
}

// async function loadModule(moduleName) {
//   try {
//     const module = await import(`./modules/${moduleName}.js`);
//     module.default(); // Assuming the module exports a default function
//   } catch (error) {
//     console.error('Failed to load module:', error);
//   }
// }

//loadModule('myDynamicModule');

export function getLastPublicAppsList() {
  const lastAppsString = localStorage.getItem("publicList-all");
  const lastAppsList = lastAppsString ? JSON.parse(lastAppsString) : [];
  // go through the list and look for apps without design info
  let hasMissingDesignInfo = false;
  lastAppsList.forEach((item) => {
    let backgroundImageUrl = item.designInfo?.backgroundImageUrl;
    if (!backgroundImageUrl) {
      item.designInfo = {
        backgroundImageUrl: getDummyImage(),
      };
      hasMissingDesignInfo = true;
    }
  });
  if (hasMissingDesignInfo) {
    // save to local storage
    localStorage.setItem("publicList-all", JSON.stringify(lastAppsList));
  }
  return lastAppsList;
}

export function getDomainFromEmail(email) {
  if (!email || !is.string(email)) return;

  let bOK = true;
  let emailAddress = email.toLowerCase();
  // check the @
  const domainSeparatorIndex = emailAddress.indexOf("@");
  if (domainSeparatorIndex === -1) bOK = false;
  // check the right side
  const domainRight =
    domainSeparatorIndex > -1
      ? emailAddress.substring(domainSeparatorIndex + 1)
      : "";
  if (!domainRight.includes(".")) bOK = false;

  // check if it is a consumer domain
  const domainPure = domainRight.split(".")[0];
  const consumerDomains = [
    "gmail",
    "hotmail",
    "yahoo",
    "live",
    "web",
    "gmx",
    "mail",
    "icloud",
    "outlook",
    "mac",
  ];
  if (consumerDomains.includes(domainPure)) bOK = false;
  if (!bOK) return emailAddress;

  return domainRight;
}

export function noCurlyBrackets(elementId) {
  if (!elementId) return "";
  return elementId.replace("{", "").replace("}", "");
}

export function getNormalizedElementId(elementId) {
  if (!elementId) return "";
  const normalizedElementId = elementId.replace("{", "").replace("}", "");
  return normalizedElementId.toLowerCase();
}

export function getNormalizedElementTypeId(elementId, elementType) {
  const normalizedElementId = getNormalizedElementId(elementId);
  if (normalizedElementId.includes(elementType + "_"))
    return normalizedElementId;
  // add the type
  const normalizedElementTypeId = elementType + "_" + normalizedElementId;
  return normalizedElementTypeId.toLowerCase();
}

export function getFileIdFromDocumentId(documentId) {
  if (!documentId?.includes("_")) return documentId;
  return documentId.substring(documentId.indexOf("_") + 1);
}

export function getElementTypeFromTypeId(elementId) {
  if (!elementId) return "range";
  let elementType = elementId.includes("sheet_") ? "sheet" : "range";
  if (elementId.includes("view_")) elementType = "view";
  return elementType;
}

export function getIdFromNormalizedElementTypeId(normalizedId) {
  if (!normalizedId) return;

  const isNoBrackets = normalizedId.includes("range_");
  const typeSeparatorIndex = normalizedId.indexOf("_");
  let elementId = normalizedId.substring(typeSeparatorIndex + 1);
  if (!isNoBrackets) {
    elementId = "{" + elementId + "}";
  }
  return elementId;
}

function getTypeFromNormalizedElementTypeId(normalizedId) {
  if (!normalizedId) return "";
  const typeSeparatorIndex = normalizedId.indexOf("_");
  if (typeSeparatorIndex === -1) return normalizedId;
  const typeName = normalizedId.substring(0, typeSeparatorIndex);
  return typeName;
}

export function getGraphElementId(elementId, elementType) {
  if (!elementId) return;

  let tempId = elementId;
  let tempType = elementType
    ? elementType
    : getTypeFromNormalizedElementTypeId(elementId);

  // special case range
  if (tempType === "range") {
    return tempId.replace("range_", "");
  }

  // is it normalized
  if (tempId.includes("_")) {
    tempId = getIdFromNormalizedElementTypeId(tempId);
  }
  // add brackets
  if (!tempId.includes("{")) {
    tempId = "{" + tempId + "}";
  }
  return tempId;
}

export function getTargetElementDirection(targetElement) {
  if (!targetElement) return;
  let direction = targetElement.field_name?.includes("result_") ? "out" : "in";
  if (
    targetElement.element &&
    ["SpreadTable", "TableView"].includes(targetElement.element)
  )
    direction = "out";
  if (
    targetElement.element &&
    ["Image", "Section", "Visibility", "Variables"].includes(
      targetElement.element,
    )
  )
    direction = "out";
  return direction;
}

export function isLayoutElement(element) {
  let isLayout = [
    "ActionButton",
    "Divider",
    "Section",
    "Spacer",
    "Title",
    "Header",
    "SingleColumnRow",
    "TwoColumnRow",
    "ThreeColumnRow",
    "FourColumnRow",
    "FiveColumnRow",
  ].includes(element);
  return isLayout;
}

export function isEditorElement(element) {
  if (!element) return false;

  const type = element?.element;
  let result = [
    "DatePicker",
    "TimePicker",
    "Dropdown",
    "TextArea",
    "RadioButtons",
    "NumberInput",
    "TextInput",
    "Checkbox",
    "Range",
  ].includes(type);
  // check the SpreadTable
  if (["SpreadTable", "TableView"].includes(type)) result = true;

  return result;
}
export function isEditableElement(element, viewType) {
  let editable = ["table", "sheet", "range"].includes(element);
  if (element === "view" && viewType) {
    editable = ["spreadsheet", "grid", "list", "kanban", "calendar"].includes(
      viewType,
    );
  }

  return editable;
}

export function convertMultiRowChildItems(childItems) {
  if (!childItems) return [];

  const items = childItems.map((childItem) => {
    if (!childItem) {
      return [];
    }
    return Array.isArray(childItem) ? childItem : [childItem];
  });

  return items;
}

export function removeItemFromArray(arr, item) {
  if (!arr || !Array.isArray(arr)) return arr;
  const index = arr.indexOf(item);
  if (index !== -1) {
    arr.splice(index, 1);
  }
  return arr;
}

// PARAMETERS

export function compressParameters(parametersJson, version = "") {
  // Encrypt your data
  let base64;
  try {
    if (version === "v2") {
      // base64 =
      //   "V2_" +
      //   CryptoJS.AES.encrypt(
      //     JSON.stringify(parametersJson),
      //     process.env.REACT_APP_URLCRYPT_KEY
      //   ).toString();
    } else {
      base64 = urlCrypt.cryptObj(parametersJson);
    }
  } catch (err) {
    console.log("ERROR compressing parameters error", err);
    base64 = "";
  }

  return base64;
}

export function decompressParameters(parametersCompressed) {
  if (!parametersCompressed) return {};
  // Decrypt compressed parametersJson
  let parametersJson;
  let parameters = removePublicMarker(parametersCompressed);
  try {
    if (parameters.startsWith("V2_")) {
      // Decrypt
      // var bytes = CryptoJS.AES.decrypt(
      //   parameters.replace("V2_", ""),
      //   process.env.REACT_APP_URLCRYPT_KEY
      // );
      // parametersJson = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } else {
      parametersJson = urlCrypt.decryptObj(parameters);
    }
  } catch (err) {
    console.log("ERROR decompressing parameters error", err);
    parametersJson = {};
  }

  return parametersJson;
}

function checkJsonParser() {
  if (window.JSON && !window.JSON.dateParser) {
    var reISO =
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
    // var reMsAjax = /^\/Date\((d|-|.*)\)[\/|\\]$/;

    JSON.dateParser = function (key, value) {
      if (typeof value === "string") {
        var a = reISO.exec(value);
        if (a) return new Date(value);
        // a = reMsAjax.exec(value);
        // if (a) {
        //     var b = a[1].split(/[-+,.]/);
        //     return new Date(b[0] ? +b[0] : 0 - +b[1]);
        // }
      }
      return value;
    };
  }
}

function getVersionOfInfo(data = "") {
  if (data.startsWith("v2_")) return "v2";
  if (data.startsWith("v3_")) return "v3";
  return "v1";
}

export function compressJson(data, options) {
  if (options === "v2") {
    const compressData = LZString.compressToEncodedURIComponent;
    const compressed = compressData(JSON.stringify(data));
    return `v2_${compressed}`;
  }

  // if (options === "v3") {
  //   const stream = new Blob([JSON.stringify(data)], {
  //     type: "application/json",
  //   }).stream();
  //   const compressedReadableStream = stream.pipeThrough(
  //     new CompressionStream("gzip"),
  //   );
  // }

  let compressedString = LZString.compressToUTF16(JSON.stringify(data));
  return compressedString;
}

function base64ToUint8Array(base64) {
  const raw = atob(base64);
  const buffer = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    buffer[i] = raw.charCodeAt(i);
  }
  return buffer;
}

export function decompressJson(data, options) {
  if (!data) return;

  // perhaps it is already a json object
  if (isObject(data)) return data;

  const version = getVersionOfInfo(data);
  let jsonData;

  // v4 compressed
  if (version === "v3") {
    const uint8Data = base64ToUint8Array(data.replace("v3_", ""));
    const decompressed = inflate(uint8Data, { to: "string" });
    const jsonData = JSON.parse(decompressed);
    return jsonData;
  }

  let decompressData =
    version === "v2"
      ? LZString.decompressFromEncodedURIComponent
      : LZString.decompressFromUTF16;
  const theData = version === "v2" ? data.slice(3) : data;
  try {
    if (options === "with-dates") {
      checkJsonParser();
      let jsonString = decompressData(theData);
      jsonData = JSON.parse(jsonString, JSON.dateParser);
      if (isJsonString(jsonData)) {
        jsonData = JSON.parse(jsonData, JSON.dateParser);
      }
    } else {
      let jsonString = decompressData(theData);
      jsonData = JSON.parse(jsonString);
      if (isJsonString(jsonData)) {
        jsonData = JSON.parse(jsonData);
      }
    }
  } catch (err) {
    console.log("ERROR decompressJson", err);
  }
  return jsonData;
}

// export function decompressJson(data, options) {
//   if (!data) return;

//   let jsonData;
//   try {
//     if (options === "with-dates") {
//       checkJsonParser();
//       let jsonString = LZString.decompressFromUTF16(data);
//       jsonData = JSON.parse(jsonString, JSON.dateParser);
//       if (isJsonString(jsonData)) {
//         jsonData = JSON.parse(jsonData, JSON.dateParser);
//       }
//     } else {
//       let jsonString = LZString.decompressFromUTF16(data);
//       jsonData = JSON.parse(jsonString);
//       if (isJsonString(jsonData)) {
//         jsonData = JSON.parse(jsonData);
//       }
//     }
//   } catch (err) {
//     console.log("ERROR decompressJson", err);
//   }
//   return jsonData;
// }

export function removePropertiesFromObject(object, list) {
  if (!list) return object;
  let newObject = jsonCopy(object);
  list.forEach((item) => {
    delete newObject[item];
  });
  return newObject;
}

// TYPES

export function getElementTypeName(type) {
  if (type === "chart") return "Chart";
  if (type === "table") return "Table";
  if (type === "sheet") return "Worksheet";
  if (type === "range") return "Range";
  if (type === "pivot") return "Pivot";
  if (type === "view") return "View";
  if (type === "grid") return "Data Grid";
  if (type === "list") return "Data List";
  if (type === "calendar") return "Calendar";
  if (type === "map") return "Map";
  if (type === "kanban") return "Kanban";
  if (type === "view") return "View";
  if (type === "calculation") return "Calculation";
  if (type === "configuration") return "Configuration";
  if (type === "template") return "Template";
  if (type === "scenario") return "Scenario";
  if (type === "element") return "Element";
  if (type === "service") return "Service";
  if (type === "recent") return "Entry";

  return "Unknown";
}

export function getElementTypeDescription(type) {
  if (type === "cell") return "a single Cell Value";
  if (type === "chart") return "a Chart";
  if (type === "table") return "a Table (database like list view)";
  if (type === "sheet") return "a Worksheet";
  if (type === "calculation") return "a Calculation";
  if (type === "configuration") return "an App configuration";
  if (type === "template") return "an App template";
  if (type === "scenario") return "a Variant";
  if (type === "list") return "a List";
  if (type === "calendar") return "a Calendar";
  if (type === "map") return "a Map";
  if (type === "range") return "a Range (an area made up of several cells)";
  if (type === "pivot") return "a Pivot View (based on a table)";
  if (type === "service") return "a Service";
  if (type === "view")
    return "a Database View (configurable grid, kanban, list, map or pivot view)";

  return "Unknown";
}

export function getEntityTypeName(type) {
  if (type === "workbook" || type === "document") return "Workbook";
  else if (type === "element") return "Element";
  else if (type === "range") return "Range";
  else if (type === "cell") return "Value";
  else if (type === "chart") return "Chart";
  else if (type === "calculation") return "App";
  else if (type === "configuration") return "Configuration";
  else if (type === "template") return "Template";
  else if (type === "scenario") return <Trans>Scenario</Trans>;
  else if (type === "sheet") return "Worksheet";
  else if (type === "table") return "Table";
  else if (type === "view") return "View";
  else if (type === "service") return "Service";
  else if (type === "recent") return "Entry";

  return "Unknown";
}

export function getEntityIcon(item) {
  if (!item) return "/images/xlsx-365.svg";

  let type = item.elementType ? item.elementType : (item.type ?? item);
  const entities = ["element", "view"];
  const elements = ["range", "cell", "chart", "table", "sheet", "calculation"];
  const isLocalFile = item && item.fileType === "localfile";

  if (isLocalFile && (type === "calculation" || type === "configuration"))
    return "/images/view-calculation-anonymous.svg";

  if (type === "view") {
    return "/images/view.svg";
  } else if (type === "scenario") {
    return "/images/view.svg";
  } else if (entities.includes(type)) {
    return "/images/xlsx-" + type + ".svg";
  } else if (elements.includes(type)) {
    return "/images/view-" + type + ".svg";
  } else if (type && ["page"].includes(type)) {
    return "/images/view-page.svg";
  }
  if (isLocalFile) return "/images/xlsx-local.svg";
  // if (isGoogleFile) return "/images/sheets.svg";

  return "/images/xlsx-365.svg";
}

export function getElementListEntityIcon(item) {
  if (!item) return "/images/xlsx-365.svg";

  let type = item.elementType ? item.elementType : (item.type ?? item);
  const entities = ["element", "view"];
  const elements = ["range", "cell", "chart", "table", "sheet", "calculation"];
  const isHostedFile =
    item.fileType === "hostedfile" ||
    (item.fileId && isHostedFileId(item.fileId));
  const isLocalFile = item.fileType === "localfile" && !isHostedFile;
  // const isGoogleFile = item.fileType === "google";

  if (isLocalFile && type === "calculation")
    return "/images/elements/thumb-calculation.webp";
  if (type === "calculation") return "/images/elements/thumb-calculation.webp";
  if (type && ["calculation", "template", "suggestion"].includes(type))
    return "/images/elements/thumb-calculation.webp";

  if (type === "view") {
    return "/images/elements/thumb-table.webp";
  } else if (type === "scenario") {
    return "/images/elements/thumb-table.webp";
  } else if (type && entities.includes(type)) {
    return "/images/elements/thumb-" + type + ".webp";
  } else if (type && elements.includes(type)) {
    return "/images/elements/thumb-" + type + ".webp";
  } else if (type && ["page"].includes(type)) {
    return "/images/view-page.svg";
  }
  if (isLocalFile) return "/images/xlsx-local.svg";
  if (isHostedFile) return "/images/xlsx-online.svg";
  // if (isGoogleFile) return "/images/sheets.svg";

  return "/images/xlsx-365.svg";
}

export function getConnectorIcon(type) {
  if (type === "table") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/connectors/hostedtable-logo.svg";
  } else if (type === "automation") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/ai-logo.svg";
  } else if (type === "lists") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/connectors/lists-logo-3.svg";
  } else if (type === "records") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/connectors/records-logo.svg";
  } else if (type === "json") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/json-logo.svg";
  } else if (type === "csv") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/csv-logo.svg";
  } else if (type === "google-analytics") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/google-analytics-logo.svg";
  } else if (type === "excel") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/excel-logo.svg";
  } else if (type === "stripe") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/stripe-logo.svg";
  } else if (type === "hubspot") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/hubspot-logo.svg";
  } else if (type === "airtable") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/airtable-logo.svg";
  } else if (type === "google-sheets") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/google-sheets-logo.svg";
  } else if (type === "salesforce") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/salesforce-logo.svg";
  } else if (type === "notion") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/notion-logo.svg";
  } else if (type === "webservice") {
    return "https://airrange-content.s3.eu-central-1.amazonaws.com/logos/webservice-logo.svg";
  }
}

export function getConnectorInfo(type) {
  let imageUrl = getConnectorIcon(type);

  if (type === "table") {
    return {
      imageUrl: imageUrl,
      title: "Table",
      description:
        "This is a 'Hosted Table' that is hosted in the EU. Data can be edited manually or using API calls.",
    };
  } else if (type === "automation") {
    return {
      imageUrl: imageUrl,
      title: "Automation",
      description: "This data was generated by an AI Wizard.",
    };
  } else if (type === "records") {
    return {
      imageUrl: imageUrl,
      title: "Records",
      description:
        "Record Tables are filled by your Apps using ActionButtons. Perfect for storing app data.",
    };
  } else if (type === "json") {
    return {
      imageUrl: imageUrl,
      title: "JSON",
      description:
        "JSON (JavaScript Object Notation) is a lightweight data interchange format.",
    };
  } else if (type === "csv") {
    return {
      imageUrl: imageUrl,
      title: "CSV",
      description:
        "CSV (Comma-Separated Values) is a simple file format used to store tabular data.",
    };
  } else if (type === "google-analytics") {
    return {
      imageUrl: imageUrl,
      title: "Google Analytics",
      description: "This data comes from Google Analytics provided by Google.",
    };
  } else if (type === "excel") {
    return {
      imageUrl: imageUrl,
      title: "Excel",
      description: "This data is from an Excel workbook in M365.",
    };
  } else if (type === "stripe") {
    return {
      imageUrl: imageUrl,
      title: "Stripe",
      description: "Stripe financial data.",
    };
  } else if (type === "hubspot") {
    return {
      imageUrl: imageUrl,
      title: "HubSpot",
      description:
        "HubSpot is a customer relationship management (CRM) platform.",
    };
  } else if (type === "airtable") {
    return {
      imageUrl: imageUrl,
      title: "Airtable",
      description: "Airtable is a cloud database.",
    };
  } else if (type === "google-sheets") {
    return {
      imageUrl: imageUrl,
      title: "Google Sheets",
      description: "Google Sheets is a web-based spreadsheet application.",
    };
  } else if (type === "salesforce") {
    return {
      imageUrl: imageUrl,
      title: "Salesforce",
      description: "Salesforce is a cloud-based software development platform.",
    };
  } else if (type === "notion") {
    return {
      imageUrl: imageUrl,
      title: "Notion",
      description:
        "Notion is a collaborative workspace for creating and organizing content.",
    };
  } else if (type === "webservice") {
    return {
      imageUrl: imageUrl,
      title: "Webservice",
      description:
        "A webservice is a software application that provides a programmatic interface for accessing data.",
    };
  }
}

export function getEntityTypeIcon(type) {
  const entities = ["element", "view"];
  const elements = ["range", "cell", "chart", "table", "sheet", "calculation"];

  if (type === "view") {
    return "/images/view.svg";
  } else if (type === "scenario") {
    return "/images/view.svg";
  } else if (entities.includes(type)) {
    return "/images/xlsx-" + type + ".svg";
  } else if (elements.includes(type)) {
    return "/images/view-" + type + ".svg";
  }
  return "/images/view.svg";
}

// ROUTES

export function getUrlParameterForDriveItemInfo(driveItemInfo, version = "") {
  return compressParameters(driveItemInfo, version);
}

function removePublicMarker(urlRoute) {
  if (!is.string(urlRoute)) return;
  let result = urlRoute
    .replace("/pub_", "/")
    .replace("/xr3b", "/")
    .replace("/xr4b", "/");

  if (result.includes("#si=")) {
    result = result.substring(0, result.indexOf("#si="));
  }

  return result;
}

export function getScenarioIdFromUrl(urlRoute) {
  if (!is.string(urlRoute)) return;

  let url = urlRoute.replace("%23", "#");
  let result;
  if (url.includes("#si=")) {
    result = url.substring(url.indexOf("#si="));
    result = result.replace("#si=", "");
  }
  return result;
}

// export function removeScenarioId(urlRoute) {
//   if (!is.string(urlRoute)) return;

//   let result = urlRoute;

//   if (urlRoute.includes("#si=")) {
//     result = urlRoute.substring(0, urlRoute.indexOf("#si="));
//   }
//   return result;
// }

export function getUrlParameterForElementJson(parametersJson, version = "") {
  return compressParameters(parametersJson, version);
}

export function getRouteShareId(urlRoute, fallbackRoute) {
  let routerUrl = window.location.hash.substr(1) ?? "";
  routerUrl = removePublicMarker(routerUrl).replace("%23", "#");
  let shareId = null;
  let hasParameters = routerUrl.includes("#");

  if (urlRoute === "invitation") {
    let parameters = routerUrl.replace("/" + urlRoute + "/", "");
    if (parameters.length < 60 || hasParameters) shareId = parameters;
  } else if (routerUrl.includes("/" + urlRoute + "/_")) {
    const parameters = routerUrl.replace("/" + urlRoute + "/_", "");
    if (parameters.length < 300 || hasParameters) shareId = parameters;
  } else if (fallbackRoute && routerUrl.includes("/" + fallbackRoute + "/")) {
    const parameters = routerUrl.replace("/" + fallbackRoute + "/_", "");
    if (parameters.length < 60 || hasParameters) shareId = parameters;
  }
  // look for scenario ID's
  if (shareId) {
    const foundIndex = shareId.indexOf("#");
    if (foundIndex > 0) {
      shareId = shareId.slice(0, foundIndex);
    }
  }

  return shareId;
}

export function getRouteShareParameter(urlRoute, fallbackRoute) {
  let routerUrl = window.location.hash.substr(1) ?? "";
  routerUrl = removePublicMarker(routerUrl).replace("%23", "#");
  let shareParameter = null;
  let shareParameterJson = null;
  let hasParameters = routerUrl.includes("#");

  if (urlRoute === "invitation") {
    let parameters = routerUrl.replace("/" + urlRoute + "/", "");
    if (parameters.length < 60 || hasParameters) shareParameter = parameters;
  } else if (routerUrl.includes("/" + urlRoute + "/_")) {
    const parameters = routerUrl.replace("/" + urlRoute + "/_", "");
    if (parameters.length < 60 || hasParameters) shareParameter = parameters;
  } else if (fallbackRoute && routerUrl.includes("/" + fallbackRoute + "/")) {
    const parameters = routerUrl.replace("/" + fallbackRoute + "/_", "");
    if (parameters.length < 60 || hasParameters) shareParameter = parameters;
  }
  // look for scenario ID's
  if (shareParameter) {
    const foundIndex = shareParameter.indexOf("#");
    if (foundIndex > 0) {
      shareParameter = shareParameter.slice(foundIndex + 1);

      if (shareParameter) {
        try {
          shareParameterJson = decompressParameters(shareParameter);
        } catch (err) {}
      }
    }
    // set a dummy parameter
    // shareParameter = {
    //   initialValues: [{ address: "Startseite!B37", value: "SDK (AO)" }],
    // };
    // let compressed = compressParameters(shareParameter);
  }

  return shareParameterJson;
}

// export function getRouteDetailId(urlRoute) {
//   let routerUrl = window.location.hash.substr(1) ?? "";
//   let detailId = null;

//   if (routerUrl.includes("/" + urlRoute + "/")) {
//     const parameters = routerUrl.replace("/" + urlRoute + "/", "");
//     if (parameters.length < 30) detailId = parameters;
//   }
//   return detailId;
// }

export function getRouteParameters(urlRoute, fallbackRoute) {
  var routerUrl = window.location.hash.substr(1);
  let parametersJson;

  if (routerUrl.includes("/" + urlRoute + "/")) {
    const compressedParameters = routerUrl.replace("/" + urlRoute + "/", "");
    parametersJson = decompressParameters(compressedParameters);
    parametersJson.isPublic = false;
  } else if (fallbackRoute && routerUrl.includes("/" + fallbackRoute + "/")) {
    const compressedParameters = routerUrl.replace(
      "/" + fallbackRoute + "/",
      "",
    );
    parametersJson = decompressParameters(compressedParameters);
    parametersJson.isPublic = true;
  }

  return parametersJson;
}

export function getElementParameters(parametersUrl) {
  if (!parametersUrl) return;
  const compressedParameters = parametersUrl.replace("/element/", "");
  const parametersJson = decompressParameters(compressedParameters);

  return parametersJson;
}

// export function getWorkbookParameters(parametersUrl) {
//   if (!parametersUrl) return;
//   const compressedParameters = parametersUrl.replace("/workbook/", "");
//   const parametersJson = decompressParameters(compressedParameters);

//   return parametersJson;
// }

// ELEMENTS

export function getElementsFromDocumentInfo(info) {
  if (!info) return [];

  let elements = [];
  info.workSheets?.forEach((item) => {
    elements.push({ id: item.elementId, name: item.name });
  });
  info.tables?.forEach((item) => {
    elements.push({ id: item.elementId, name: item.name });
  });
  info.ranges?.forEach((item) => {
    elements.push({ id: item.elementId, name: item.name });
  });
  info.appInfo?.views?.forEach((item) => {
    elements.push({ id: item.elementId, name: item.name });
  });
  return elements;
}

// childItems can have arrays of childItems
export function getSingleIdsFromMultiArray(childItems) {
  let subItems = [];

  if (!is.array(childItems)) return subItems;

  childItems.forEach((elementId) => {
    // childItems can be an array or a string
    if (is.array(elementId)) {
      elementId.forEach((subId) => {
        if (is.string(subId)) subItems.push(subId);
      });
    }
    if (is.string(elementId)) {
      subItems.push(elementId);
    }
  });
  return subItems;
}

// TEXTS

export function truncate(str, n, useWordBoundary) {
  if (!str) return;
  if (str.length <= n) {
    return str;
  }
  const subString = str.substr(0, n - 1); // the original check
  return (
    (useWordBoundary
      ? subString.substr(0, subString.lastIndexOf(" "))
      : subString) + "..."
  );
}

// export function getValueAsString(value, n) {
//   let tempValue = String(value);
//   if (isValidDate(value)) {
//     tempValue = formatDateTimeNatural(value);
//   }
//   return tempValue;
// }

export function isHostedFileId(fileId) {
  if (!fileId) return false;
  return fileId.endsWith("_hosted");
}

export function isEmailAddress(email) {
  if (!email) return false;

  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}

// export function isUrl(str) {
//   var pattern = new RegExp(
//     "^(https?:\\/\\/)?" + // protocol
//       "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|" + // domain name
//       "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
//       "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
//       "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
//       "(\\#[-a-z\\d_]*)?$",
//     "i"
//   ); // fragment locator
//   return pattern.test(str);
// }

export function getDataType(value) {
  if (is.number(value)) return "number";
  if (is.date(value)) return "date";
  if (is.bool(value)) return "bool";
  if (is.string(value)) return "string";

  return undefined;
}

export function getPivotSummaryType(value) {
  if (is.number(value)) return "sum";
  // if (is.date(value)) return "count";
  // if (is.bool(value)) return "count";
  // if (is.string(value)) return "count";

  return undefined;
}

export function getDecimalCountFromFormat(format) {
  try {
    if (typeof format !== "string") return 2;

    const decimalPart = format.split(".")[1];
    if (!decimalPart) return 0;

    const cleanPart = decimalPart.replace(/[^0#]/g, ""); // Remove %, a, $, etc.
    return cleanPart.length || 0;
  } catch (e) {
    return 2;
  }
}

export function getFieldInfo(fieldName, fieldValue) {
  let fieldContent;
  let fieldCaption = fieldName;
  let dataType = getDataType(fieldValue);
  if (dataType === "string" && fieldValue) {
    // check url
    // if (isUrl(fieldValue)) fieldContent = "url";
    // check image url
    let isImageUrl =
      typeof fieldValue === "string" &&
      fieldValue.toLowerCase().includes("//") &&
      (fieldValue.toLowerCase().includes(".png") ||
        fieldValue.toLowerCase().includes(".jpg"));
    if (isImageUrl) fieldContent = "image";
    // check email
    if (isEmailAddress(fieldValue)) fieldContent = "email";
    // check phone
    if (
      !fieldContent &&
      fieldName &&
      (fieldName.includes("phone") || fieldName.includes("mobile"))
    )
      fieldContent = "phone";
  }
  if (dataType === "number") {
    if (fieldName.toLowerCase().includes("long")) fieldContent = "longitude";
    if (fieldName.toLowerCase().includes("lat")) fieldContent = "latitude";
    //if (fieldValue > 40000) fieldContent = "date";
  }

  return {
    fieldName: fieldName,
    fieldValue: fieldValue,
    fieldContent: fieldContent,
    fieldCaption: fieldCaption,
    dataType: dataType,
  };
}

export function getObjectSize(data) {
  try {
    return JSON.stringify(data).length;
  } catch (err) {
    console.log("ERROR getObjectSize length", err);
  }
}

// NUMBERS

export function getFormatedNumber(value, numberFormat, prefix, suffix) {
  if (!numberFormat || value === undefined || value === null) return value;
  let formatedValue;
  const prefixText = prefix ? prefix : "";
  const suffixText = suffix ? suffix : "";

  let numberFormatCheck = numberFormat.toLowerCase();
  try {
    if (
      numberFormatCheck.includes("d") ||
      numberFormatCheck.includes("m") ||
      numberFormatCheck.includes("y")
    ) {
      // date format
      formatedValue = ExcelDateToFormatedString(value, numberFormat);
    } else {
      formatedValue = numeral(value).format(numberFormat);
    }
  } catch (err) {
    formatedValue = value?.toString() || "";
  }

  return prefixText + formatedValue + suffixText;
}

export function getLocalDateFormat(dateSelector = "date") {
  const date = new Date(2013, 11, 31);
  const options = {
    year: "numeric",
    ...(dateSelector !== "year" && { month: "numeric" }),
    ...(dateSelector === "date" && { day: "numeric" }),
  };
  let str = date.toLocaleDateString(undefined, options);
  str = str.replace("2013", "YYYY");
  str = str.replace("12", "MM");
  str = str.replace("31", "DD");
  return str;
}

export function getLanguageFromCode(languageCode) {
  let language = "en";
  if (!languageCode || typeof languageCode !== "string") return language;

  if (languageCode.startsWith("de-")) language = "de";
  if (languageCode.startsWith("fr-")) language = "fr";
  if (languageCode.startsWith("es-")) language = "es";
  if (languageCode.startsWith("pt-")) language = "pt";
  return language;
}

function xorEncrypt(text, key) {
  if (!key) return text;

  let result = "";
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    );
  }
  return result;
}

export function base64UrlToJson(base64String, encryptionKey) {
  try {
    // Convert from URL-safe base64 back to standard base64
    let base64 = base64String.replace(/-/g, "+").replace(/_/g, "/");
    // Add padding if necessary
    while (base64.length % 4) {
      base64 += "=";
    }
    let jsonString = atob(base64);
    if (encryptionKey) {
      jsonString = xorEncrypt(jsonString, encryptionKey);
    }
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Error decoding base64 to JSON:", error);
    throw error;
  }
}

export function optimizeSpreadCellValue(value) {
  let newValue = value;
  // check for date value
  if (is.string(newValue)) {
    const isValueADate = !isNaN(new Date(newValue));
    if (isValueADate) newValue = new Date(newValue);
  }
  return newValue;
}

export function getDayDifferenceToEndDate(endDate) {
  // is there an end date
  if (isValidDate(endDate)) {
    const todayDate = new Date();
    const dayDifference = differenceInCalendarDays(endDate, todayDate);
    return dayDifference;
  }
  return null;
}

export function isInThePast(dateValue) {
  const diff = getDayDifferenceToEndDate(dateValue);
  return diff < 0;
}

export function isLessThanXMinutes(dateValue, minutes) {
  const diff = differenceInMinutes(dateValue, new Date());
  return diff > -minutes;
}

// export function getTimestamp(resetMarkInMinutes = 5) {
//   const now = new Date();
//   now.setMilliseconds(0);
//   now.setSeconds(0);
//   // Resets every 5 minutes
//   const minuteMark = now.getMinutes() - (now.getMinutes() % resetMarkInMinutes);
//   return now.setMinutes(minuteMark) / 1000;
// }

// export function getTimestampForLog() {
//   const now = new Date();
//   return format(now, "yyyy-MM-dd HH:mm:ss");
// }

export function formatDateTimeWithFormat(value, date_format) {
  let result = value;
  try {
    result = format(value, date_format);
  } catch (err) {
    console.log("ERROR formatDateTimeWithFormat", date_format, err);
  }
  return result;
}

export function formatDateTimeForLog(value) {
  if (!value) return;
  return format(value, "yyyy-MM-dd HH:mm:ss");
}

export function formatDateForLog(value) {
  if (!value) value = new Date();
  return format(value, "yyyy-MM-dd");
}

export function formatDateTimeForFileName(value) {
  if (!value) value = new Date();
  return format(value, "yy-MM-dd-HH-mm");
}

export function formatDateTimeForCalendar(value) {
  return format(value, "yyyy-MM-dd");
}

// export function formatTime(value) {
//   if (!value) return;
//   return format(value, "HH:mm:ss");
// }

// export function formatDateTimeNatural(value) {
//   if (value > 0) {
//     return format(value, "dd/MM/yyyy");
//     // return format(newDate, "yyyy-MM-dd", { locale: de });
//   } else {
//     let tempDate = new Date(value);
//     if (isValidDate(tempDate)) {
//       return format(tempDate, "dd/MM/yyyy", { locale: de });
//     } else return "";
//   }
// }

export function formatDateTimeFull(value) {
  if (value > 0) {
    return format(value, "EEEE, dd/MM/yyyy HH:mm");
    // return format(newDate, "yyyy-MM-dd", { locale: de });
  } else {
    let tempDate = new Date(value);
    if (isValidDate(tempDate)) {
      return format(tempDate, "EEEE, dd/MM/yyyy HH:mm", { locale: de });
    } else return "";
  }
}

// export function formatDateTimeFullFromString(valueString) {
//   const value = new Date(valueString);
//   if (value > 0) {
//     return format(value, "EEEE, dd/MM/yyyy HH:mm");
//     // return format(newDate, "yyyy-MM-dd", { locale: de });
//   } else {
//     let tempDate = new Date(value);
//     if (isValidDate(tempDate)) {
//       return format(tempDate, "EEEE, dd/MM/yyyy", { locale: de });
//     } else return "";
//   }
// }

// export function formatDateTimeFromString(valueString) {
//   const value = new Date(valueString);
//   if (value > 0) {
//     return format(value, "dd/MM/yyyy HH:mm");
//     // return format(newDate, "yyyy-MM-dd", { locale: de });
//   } else {
//     let tempDate = new Date(value);
//     if (isValidDate(tempDate)) {
//       return format(tempDate, "dd/MM/yyyy", { locale: de });
//     } else return "";
//   }
// }

export function formatDateFull(value) {
  if (value > 0) {
    return format(value, "EEEE, dd/MM/yyyy");
    // return format(newDate, "yyyy-MM-dd", { locale: de });
  } else {
    let tempDate = new Date(value);
    if (isValidDate(tempDate)) {
      return format(tempDate, "EEEE, dd.MM.yyyy", { locale: de });
    } else return "";
  }
}

export function waitMilliSeconds(milliseconds) {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
}

export function formatDateShort(value) {
  if (!isValidDate(value)) {
    return null;
  }

  if (value > 0) {
    return format(value, "dd/MM");
  } else {
    let tempDate = new Date(value);
    if (isValidDate(tempDate)) {
      return format(tempDate, "dd/MM");
    } else return "";
  }
}

export function formatDate(value) {
  try {
    let dateFormat = getLocalDateFormat()
      .replaceAll("YY", "yy")
      .replace("DD", "dd");

    return format(value, dateFormat);
  } catch (error) {
    return "---";
  }
}

// export function formatDateShort(value) {
//   if (value > 0) {
//     return format(value, "dd/MM/yy");
//     // return format(newDate, "yyyy-MM-dd", { locale: de });
//   } else {
//     let tempDate = new Date(value);
//     if (isValidDate(tempDate)) {
//       return format(tempDate, "dd/MM/yy", { locale: de });
//     } else return "";
//   }
// }

// export function formatDateLong(value) {
//   if (value > 0) {
//     return format(value, "d. MMMM yyyy");
//     // return format(newDate, "yyyy-MM-dd", { locale: de });
//   } else {
//     let tempDate = new Date(value);
//     if (isValidDate(tempDate)) {
//       return format(tempDate, "d. MMMM yyyy", { locale: de });
//     } else return "";
//   }
// }

// export function formatDateTimeMonth(value) {
//   if (value > 0) {
//     return format(value, "yyyy/MM");
//     //    return format(newDate, "yyyy-MM-dd", { locale: de });
//   } else {
//     return "";
//   }
// }

export function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

// EXCEL FUNCTIONALITY

// export function getTimeStampAsString(excelTimestamp) {
//   if (excelTimestamp > 0) {
//     const newDate = new Date(getJsDateFromExcel(excelTimestamp));
//     return format(newDate, "yyyy-MM-dd", { locale: de });
//   } else {
//     return "";
//   }
// }

// export function getDateFromExcel(excelDate) {
//   if (excelDate) {
//     return new Date(excelDate);
//   } else {
//     return undefined;
//   }
// }

// export function getCurrencyAsString(value) {
//   let tempValue = value;
//   if (isNaN(tempValue)) {
//     tempValue = Number(value);
//   }

//   try {
//     const newValue = tempValue.toLocaleString("de-DE");
//     return newValue !== "0" ? newValue : "";
//   } catch (err) {
//     console.log("ERROR getCurrencyAsString", err);
//   }
// }

export function getIdFromCaption(caption) {
  if (!caption || typeof caption !== "string") return "";

  return (
    caption
      .replaceAll(" ", "_")
      .replaceAll(/\u00dc/g, "Ue")
      .replaceAll(/\u00c4/g, "Ae")
      .replaceAll(/\u00d6/g, "Oe")
      .replaceAll(/\u00fc/g, "ue")
      .replaceAll(/\u00e4/g, "ae")
      .replaceAll(/\u00f6/g, "oe")
      .replaceAll(/\u00df/g, "ss")
      // eslint-disable-next-line no-control-regex
      .replaceAll(/[^\x00-\x7F]/g, "")
      .replaceAll(/[^a-z0-9]/gi, "")
      .toLowerCase()
  );
}

// export function removeSpecialCharacters(caption) {
//   return (
//     caption
//       .replaceAll(" ", "_")
//       // .replaceAll(/\u00dc/g, "Ue")
//       // .replaceAll(/\u00c4/g, "Ae")
//       // .replaceAll(/\u00d6/g, "Oe")
//       // .replaceAll(/\u00fc/g, "ue")
//       // .replaceAll(/\u00e4/g, "ae")
//       // .replaceAll(/\u00f6/g, "oe")
//       // .replaceAll(/\u00df/g, "ss")
//       // eslint-disable-next-line no-control-regex
//       .replaceAll(/[^\x00-\x7F]/g, "")
//       .replaceAll(/[^a-z0-9]/gi, "")
//   );
// }
// ARRAYS

export function moveArrayItemToNewIndex(arr, old_index, new_index) {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr;
}

export function objectArrayGroupBy(list, keyGetter) {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

// export function arrayMoveTo(arr, fromIndex, toIndex) {
//   if (!arr) return;

//   var element = arr[fromIndex];
//   arr.splice(fromIndex, 1);
//   arr.splice(toIndex, 0, element);
//   return arr;
// }

export function getFileInFileListCount(list, fileName) {
  if (!is.array(list)) return -1;
  if (!is.string(fileName)) return -1;

  let files = [];
  try {
    files = list.filter((obj) => {
      return obj.name.toLowerCase().includes(fileName.toLowerCase());
    });
  } catch (err) {
    console.log("ERROR file filter", err);
  }
  return files.length;
}

export async function getGroupedChanges(changes, field) {
  let groupedChanges = [];
  const grouped = objectArrayGroupBy(changes, (entry) => entry[field]);
  grouped.forEach((group) => {
    const value = group.length;
    const count = group.length;
    const date = group[0][field];
    const groupValue = group[0][field];
    groupedChanges.push({ groupValue, count, value, date });
  });
  return groupedChanges;
}

export async function getGroupByData(list, field) {
  let groupedChanges = [];
  const grouped = objectArrayGroupBy(list, (entry) => entry[field]);
  grouped.forEach((group) => {
    const value = group.length;
    const count = group.length;
    const date = group[0][field];
    const groupValue = group[0][field];
    groupedChanges.push({ groupValue, count, value, date });
  });
  return groupedChanges;
}

export function numberFromText(text) {
  // numberFromText("AA");
  const charCodes = text
    .split("") // => ["A", "A"]
    .map((char) => char.charCodeAt(0)) // => [65, 65]
    .join(""); // => "6565"
  return parseInt(charCodes, 10);
}

export function numberOfDecimals(value) {
  if (!value || !is.number(value)) return 0;
  const int_part = Math.trunc(value);
  const float_part = Number((value - int_part).toFixed(2));
  let result = float_part.toString();
  if (result === "0") return 0;
  let length = result.length;
  if (length > 2) {
    length = length - 2;
  }
  return length;
}
